import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { MultiColumnList, TextLink } from '@folio/stripes/components';
import { FormattedMessage, useIntl } from 'react-intl';
import { branding } from 'stripes-config';
import { useStripes } from '@folio/stripes-core';
import moment from 'moment';
import useFieldLabelsAPI from '../../../../../hooks/useFieldLabelsAPI';
import { detectReportTypeFromResultSet } from '../../../../../helpers/reportTypeDetector';
import {
  buildAnnotationMap,
  buildFormattingOptions,
  createFieldFormatter,
  formatNumber,
} from '../../../../../helpers/fieldValueFormatter';
import DrillDownManager from '../../core/DrillDownManager';
import css from './styles.css';

function GenericTableView({
  resultSet,
  onDrillDown,
  drillDownMeasures = [],
  hiddenFields = [],
  displaySettings,
  printOptions,
  reportType,
  query,
  setQuery,
  formatting,
  fieldAliasMap = {},
}) {
  const [contentData, setContentData] = useState([]);
  const [visibleColumns, setVisibleColumns] = useState([]);
  const [detectedReportType, setDetectedReportType] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 100;

  const annotationMap = useMemo(
    () => buildAnnotationMap(resultSet),
    [resultSet],
  );

  const { labelsMap: dimLabels } = useFieldLabelsAPI({
    fieldType: 'dimension',
  });
  const { labelsMap: measLabels } = useFieldLabelsAPI({ fieldType: 'measure' });
  const labelsMap = { ...dimLabels, ...measLabels };

  const intl = useIntl();
  const fmtOptions = useMemo(
    () => buildFormattingOptions(formatting, intl),
    [formatting, intl],
  );

  // Resolve column header label:
  // column key is "cataloging_view_material_type" (dots replaced with underscores)
  // original key is "cataloging_view.material_type"
  // fieldAliasMap["cataloging_view.material_type"] = "material_type.name"
  // labelsMap["material_type.name"] = "نوع المادة"
  const resolveColumnLabel = (columnKey) => {
    // Restore original fieldKey (replace first underscore-separated view prefix)
    const parts = columnKey.split('_');
    for (let i = 1; i < parts.length; i++) {
      const candidate =
        parts.slice(0, i).join('_') + '.' + parts.slice(i).join('_');
      const alias = fieldAliasMap?.[candidate];
      if (alias && labelsMap?.[alias]) return labelsMap[alias];
      if (labelsMap?.[candidate]) return labelsMap[candidate];
    }
    // Fallback: humanize
    const bare = parts[parts.length - 1];
    return bare.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const buildColumnMapping = (columns) => {
    const mapping = { rowIndex: '#' };
    columns.forEach((col) => {
      mapping[col] = resolveColumnLabel(col);
    });
    return mapping;
  };

  const {
    user: {
      user: { firstName, lastName },
    },
  } = useStripes();

  // Create custom formatter with drill-down support
  const createCustomFormatter = () => {
    const offset = query?.offset || 0;
    const customFormatter = {
      rowIndex: (item) => offset + item.rowIndex + 1,
    };

    // Get drill-down measures from DrillDownManager
    const activeDimensions = resultSet?.loadResponses?.[0]?.dimensions || [];

    // Create formatters for all visible columns
    visibleColumns.forEach((column) => {
      // Skip if formatter already exists
      if (customFormatter[column]) {
        return;
      }

      // Check if this column supports drill-down via annotationMap.drillMembers
      if (DrillDownManager.supportsDrillDown(column, annotationMap) && onDrillDown) {
        customFormatter[column] = (item) => {
          const value = item[column];
          if (!value || value === '0') return formatNumber(value, fmtOptions);

          return (
            <TextLink
              onClick={() => {
                const drillDownData = DrillDownManager.buildDrillDownQuery(
                  column,
                  item,
                  annotationMap,
                  activeDimensions,
                );
                if (drillDownData) onDrillDown({ ...drillDownData, fieldAliasMap });
              }}
              style={{ cursor: 'pointer' }}
            >
              {formatNumber(value, fmtOptions)}
            </TextLink>
          );
        };
      } else {
        // Check if this is a reference table field
        const refFormatter = createFieldFormatter(
          column,
          annotationMap,
          fmtOptions,
        );
        if (refFormatter) {
          customFormatter[column] = refFormatter;
        } else {
          // Apply formatting using annotationMap + fmtOptions (works in all modes)
          customFormatter[column] = createFieldFormatter(
            column,
            annotationMap,
            fmtOptions,
          );
        }
      }
    });

    return customFormatter;
  };

  // Process result set data
  useEffect(() => {
    const data = [];
    if (resultSet && resultSet.loadResponses?.length > 0) {
      const detected = detectReportTypeFromResultSet(resultSet);
      if (detected) {
        setDetectedReportType(detected);
      }

      resultSet.loadResponses[0].data.forEach((result, index) => {
        const obj = { rowIndex: index };
        for (const key in result) {
          if (key) {
            obj[key.replace('.', '_')] = result[key];
          }
        }
        data.push(obj);
      });

      setContentData(data);
      const total = resultSet.loadResponses[0].total || data.length;
      setTotalCount(total);

      const columns = resultSet.loadResponses[0].data.length
        ? Object.keys(resultSet.loadResponses[0].data[0])
        : [];

      setVisibleColumns(
        columns
          .map((col) => col.replace('.', '_'))
          .filter((col) => !hiddenFields.includes(col)),
      );
    } else {
      setContentData([]);
      setVisibleColumns([]);
      setTotalCount(0);
    }
  }, [resultSet, hiddenFields, query, pageSize]);

  const handleNeedMoreData = (_askAmount, index) => {
    if (!query || !setQuery) return;
    setQuery({
      ...query,
      offset: index,
      limit: pageSize,
    });
  };

  const renderPrintVersion = () => (
    <div style={{ overflow: 'hidden', height: 0 }}>
      <div ref={printOptions?.componentRef} dir="rtl">
        <div className={css.header_container}>
          <div className={css.branding_container}>
            <div className={css.logo}>
              <img alt={branding.logo.alt} src={branding.logo.src} />
            </div>
            <div className={css.org}>
              <img alt={branding.org.alt} src={branding.org.src} />
            </div>
          </div>
          <h2 className={css.paneTitle}>{printOptions?.reportName}</h2>
          <hr className={css.divider} />
        </div>
        <div style={{ width: '100%' }}>
          <MultiColumnList
            id="report-result-print-Multi-Column-List"
            interactive={false}
            visibleColumns={['rowIndex', ...visibleColumns]}
            contentData={contentData}
            columnMapping={buildColumnMapping(visibleColumns)}
            formatter={createCustomFormatter()}
          />
        </div>
        <div className={css.footer}>
          <div>
            <FormattedMessage
              id="ui-reports.printWindow.createdBy"
              values={{ createdBy: `${firstName} ${lastName}` }}
              defaultMessage="Created by: {createdBy}"
            />
          </div>
          <div>
            <FormattedMessage
              id="ui-reports.printWindow.createdDate"
              values={{ createdDate: moment().toString() }}
              defaultMessage="Created date: {createdDate}"
            />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <MultiColumnList
        id="generic-table-view-multi-column-list"
        interactive={false}
        visibleColumns={['rowIndex', ...visibleColumns]}
        contentData={contentData}
        columnMapping={buildColumnMapping(visibleColumns)}
        formatter={createCustomFormatter()}
        autosize={displaySettings?.autosize}
        maxHeight={
          displaySettings?.autosize ? null : displaySettings?.maxHeight
        }
        virtualize={displaySettings?.autosize}
        pagingType="prev-next"
        pageAmount={pageSize}
        totalCount={totalCount}
        pagingOffset={query?.offset || 0}
        onNeedMoreData={handleNeedMoreData}
      />
      {printOptions?.isShowPrintWindow && renderPrintVersion()}
    </>
  );
}

GenericTableView.propTypes = {
  resultSet: PropTypes.object.isRequired,
  onDrillDown: PropTypes.func,
  drillDownMeasures: PropTypes.arrayOf(PropTypes.string),
  hiddenFields: PropTypes.arrayOf(PropTypes.string),
  displaySettings: PropTypes.shape({
    autosize: PropTypes.bool,
    maxHeight: PropTypes.number,
  }),
  printOptions: PropTypes.shape({
    componentRef: PropTypes.object,
    isShowPrintWindow: PropTypes.bool,
    reportName: PropTypes.string,
  }),
  reportType: PropTypes.string,
  query: PropTypes.object,
  setQuery: PropTypes.func,
  formatting: PropTypes.shape({
    numberFormat: PropTypes.string,
    dateFormat: PropTypes.string,
    currencyFormat: PropTypes.shape({ symbol: PropTypes.string }),
  }),
  fieldAliasMap: PropTypes.object,
};

GenericTableView.defaultProps = {
  drillDownMeasures: [],
  hiddenFields: [],
  displaySettings: { autosize: true, maxHeight: null },
  printOptions: {},
};

export default GenericTableView;
